import { create } from 'zustand';
import {
  UserProfile,
  Expense,
  Milestone,
  FlightHours,
  CertificateType,
  ExpenseCategory
} from '../types';
import { supabase, getCurrentUser, isAuthenticated } from '../lib/supabase';

// Default milestones for PPL
const DEFAULT_MILESTONES: Milestone[] = [
  { id: 1, name: 'Medical Certificate', status: 'not_started', cost: 150 },
  { id: 2, name: 'FAA Written Exam', status: 'not_started', cost: 175 },
  { id: 3, name: 'First Solo Flight', status: 'not_started', cost: 0 },
  { id: 4, name: 'Solo Cross-Country', status: 'not_started', cost: 0 },
  { id: 5, name: 'Night Requirements', status: 'not_started', cost: 0 },
  { id: 6, name: 'Checkride', status: 'not_started', cost: 800 },
  { id: 7, name: 'Certificate Earned! 🎉', status: 'not_started', cost: 0 },
];

interface BenchmarkData {
  location: string;
  flightSchool: string;
  aircraftType: string;
  totalHours: number;
  totalCost: number;
  hourlyRate: number;
  instructorRate: number;
  fuelCost: number;
  rentalType: 'wet' | 'dry';
  certificationGoal: 'private' | 'instrument' | 'commercial' | 'cfi';
  trainingDuration: number;
  satisfaction: number;
  wouldRecommend: boolean;
  submittedAt: string;
}

interface FlightCentsStore {
  // State
  profile: UserProfile | null;
  expenses: Expense[];
  milestones: Milestone[];
  flightHours: FlightHours;
  anonymousBenchmarks: BenchmarkData[];
  loading: boolean;
  error: string | null;

  // Actions
  initializeApp: () => Promise<void>;
  setProfile: (profile: UserProfile) => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  clearProfile: () => Promise<void>;
  addExpense: (expense: Omit<Expense, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  updateExpense: (id: string, updatedExpense: Partial<Expense>) => Promise<void>;
  updateMilestone: (id: number, updates: Partial<Milestone>) => Promise<void>;
  updateFlightHours: (hours: Partial<FlightHours>) => Promise<void>;
  addAnonymousBenchmark: (data: Omit<BenchmarkData, 'submittedAt'>) => Promise<void>;
  getRegionalBenchmarks: (location: string) => BenchmarkData[];
  clearAllData: () => Promise<void>;
  fetchUserData: () => Promise<void>;
}

export const useFlightCentsStore = create<FlightCentsStore>((set, get) => ({
  profile: null,
  expenses: [],
  milestones: DEFAULT_MILESTONES,
  flightHours: {
    totalFlightHours: 0,
    totalGroundHours: 0,
    soloHours: 0,
    crossCountryHours: 0,
    nightHours: 0,
    instrumentHours: 0,
  },
  anonymousBenchmarks: [],
  loading: true, // Start loading by default to prevent auth race conditions
  error: null,

  initializeApp: async () => {
    try {
      set({ loading: true, error: null });

      const authenticated = await isAuthenticated();
      if (!authenticated) {
        set({ loading: false });
        return;
      }

      await get().fetchUserData();
      set({ loading: false });
    } catch (error: any) {
      console.error('Initialize app error:', error);
      set({ error: error.message, loading: false });
    }
  },

  fetchUserData: async () => {
    try {
      const user = await getCurrentUser();
      if (!user) {
        set({ profile: null, expenses: [], milestones: DEFAULT_MILESTONES });
        return;
      }

      // Fetch profile
      console.log('Fetching profile for user:', user.id);
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileData) {
        console.log('✅ Profile found:', profileData);
      } else {
        console.log('⚠️ No profile found for user (redirect to setup expected)');
      }

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('❌ Profile fetch error:', profileError);
        throw profileError;
      }

      // Fetch expenses
      const { data: expensesData, error: expensesError } = await supabase
        .from('expenses')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (expensesError) throw expensesError;

      // Fetch milestones
      const { data: milestonesData, error: milestonesError } = await supabase
        .from('milestones')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true });

      if (milestonesError) throw milestonesError;

      // Fetch subscription
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (subscriptionError) throw subscriptionError;

      // Determine tier from price ID
      let subscriptionTier: 'free' | 'pro' | 'premium' = 'free';
      if (subscriptionData?.price_id) {
        // We need to import SUBSCRIPTION_PLANS to check against price IDs
        // But to avoid circular deps or complex logic here, we'll do a simple mapping
        // ideally we imported SUBSCRIPTION_PLANS
      }

      // Fetch flight hours
      const { data: hoursData, error: hoursError } = await supabase
        .from('flight_hours')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (hoursError && hoursError.code !== 'PGRST116') {
        throw hoursError;
      }

      set({
        profile: profileData ? {
          userId: profileData.user_id,
          name: profileData.name || user.email?.split('@')[0] || 'Pilot',
          location: profileData.location || '',
          certificateType: profileData.certificate_type as CertificateType,
          totalBudget: Number(profileData.total_budget),
          targetDate: profileData.target_date || '',
          createdAt: profileData.created_at,
          // Subscription data
          subscriptionTier: 'free', // Default, will update below if valid
          subscriptionStatus: subscriptionData?.status,
          subscriptionExpiresAt: subscriptionData?.current_period_end,
          subscriptionId: subscriptionData?.paddle_subscription_id,
        } : null,
        expenses: expensesData || [],
        milestones: milestonesData?.length ? milestonesData.map(m => ({
          id: Number(m.id),
          name: m.name,
          status: m.status as 'not_started' | 'in_progress' | 'completed',
          cost: Number(m.cost),
          completedDate: m.completed_date,
        })) : DEFAULT_MILESTONES,
        flightHours: hoursData ? {
          totalFlightHours: Number(hoursData.total_flight_hours),
          totalGroundHours: Number(hoursData.total_ground_hours),
          soloHours: Number(hoursData.solo_hours),
          crossCountryHours: Number(hoursData.cross_country_hours),
          nightHours: Number(hoursData.night_hours),
          instrumentHours: Number(hoursData.instrument_hours),
        } : get().flightHours,
      });

      // Update tier if we have a valid subscription
      if (get().profile && subscriptionData && subscriptionData.status === 'active') {
        // Determine tier dynamically based on config price IDs
        const config = (await import('../config')).default;
        let tier: 'free' | 'pro' | 'premium' = 'free';

        if (subscriptionData.price_id === config.payments.paddle.priceIdPro) {
          tier = 'pro';
        } else if (subscriptionData.price_id === config.payments.paddle.priceIdPremium) {
          tier = 'premium';
        }

        set({
          profile: { ...get().profile!, subscriptionTier: tier }
        });
      }
    } catch (error: any) {
      console.error('Fetch user data error:', error);
      set({ error: error.message });
    }
  },

  setProfile: async (profile) => {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          name: profile.name || user.email?.split('@')[0] || 'Pilot',
          location: profile.location || null,
          certificate_type: profile.certificateType,
          total_budget: profile.totalBudget,
          certificate_type: profile.certificateType,
          total_budget: profile.totalBudget,
          target_date: profile.targetDate || null,
        }, { onConflict: 'user_id' });

      if (error) {
        console.error('❌ Profile save error:', error);
        throw error;
      }

      console.log('✅ Profile saved successfully');

      // Create default milestones
      const { error: milestonesError } = await supabase
        .from('milestones')
        .insert(
          DEFAULT_MILESTONES.map((m, index) => ({
            user_id: user.id,
            name: m.name,
            status: m.status,
            cost: m.cost,
            sort_order: index,
          }))
        );

      if (milestonesError) throw milestonesError;

      // Create flight hours record
      const { error: hoursError } = await supabase
        .from('flight_hours')
        .insert({
          user_id: user.id,
        });

      if (hoursError) throw hoursError;

      set({ profile });
      await get().fetchUserData();
    } catch (error: any) {
      console.error('Set profile error:', error);
      set({ error: error.message });
      throw error;
    }
  },

  updateProfile: async (updates) => {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update({
          certificate_type: updates.certificateType,
          total_budget: updates.totalBudget,
          target_date: updates.targetDate || null,
        })
        .eq('user_id', user.id);

      if (error) throw error;

      set({ profile: { ...get().profile!, ...updates } });
    } catch (error: any) {
      console.error('Update profile error:', error);
      set({ error: error.message });
      throw error;
    }
  },

  clearProfile: async () => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      set({ profile: null, expenses: [], milestones: DEFAULT_MILESTONES });
    } catch (error: any) {
      console.error('Clear profile error:', error);
      set({ error: error.message });
    }
  },

  addExpense: async (expense) => {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('expenses')
        .insert({
          user_id: user.id,
          ...expense,
        })
        .select()
        .single();

      if (error) throw error;

      set({ expenses: [data, ...get().expenses] });

      // Update flight hours if applicable
      if (expense.flightHours && expense.flightHours > 0) {
        const currentHours = get().flightHours;
        await get().updateFlightHours({
          totalFlightHours: currentHours.totalFlightHours + expense.flightHours,
        });
      }
    } catch (error: any) {
      console.error('Add expense error:', error);
      set({ error: error.message });
      throw error;
    }
  },

  deleteExpense: async (id) => {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      set({ expenses: get().expenses.filter(e => e.id !== id) });
    } catch (error: any) {
      console.error('Delete expense error:', error);
      set({ error: error.message });
      throw error;
    }
  },

  updateExpense: async (id, updatedExpense) => {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('expenses')
        .update(updatedExpense)
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      set({
        expenses: get().expenses.map(e =>
          e.id === id ? { ...e, ...updatedExpense } : e
        ),
      });
    } catch (error: any) {
      console.error('Update expense error:', error);
      set({ error: error.message });
      throw error;
    }
  },

  updateMilestone: async (id, updates) => {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('milestones')
        .update({
          name: updates.name,
          status: updates.status,
          cost: updates.cost,
          completed_date: updates.completedDate || null,
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      set({
        milestones: get().milestones.map(m =>
          m.id === id ? { ...m, ...updates } : m
        ),
      });
    } catch (error: any) {
      console.error('Update milestone error:', error);
      set({ error: error.message });
      throw error;
    }
  },

  updateFlightHours: async (hours) => {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      const updatedHours = { ...get().flightHours, ...hours };

      const { error } = await supabase
        .from('flight_hours')
        .update({
          total_flight_hours: updatedHours.totalFlightHours,
          total_ground_hours: updatedHours.totalGroundHours,
          solo_hours: updatedHours.soloHours,
          cross_country_hours: updatedHours.crossCountryHours,
          night_hours: updatedHours.nightHours,
          instrument_hours: updatedHours.instrumentHours,
        })
        .eq('user_id', user.id);

      if (error) throw error;

      set({ flightHours: updatedHours });
    } catch (error: any) {
      console.error('Update flight hours error:', error);
      set({ error: error.message });
      throw error;
    }
  },

  addAnonymousBenchmark: async (data) => {
    try {
      const user = await getCurrentUser();

      const benchmark: BenchmarkData = {
        ...data,
        submittedAt: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('benchmark_submissions')
        .insert({
          user_id: user?.id || null,
          location: data.location,
          flight_school: data.flightSchool,
          aircraft_type: data.aircraftType,
          total_hours: data.totalHours,
          total_cost: data.totalCost,
          hourly_rate: data.hourlyRate,
          instructor_rate: data.instructorRate,
          fuel_cost: data.fuelCost,
          rental_type: data.rentalType,
          certification_goal: data.certificationGoal,
          training_duration: data.trainingDuration,
          satisfaction: data.satisfaction,
          would_recommend: data.wouldRecommend,
        });

      if (error) throw error;

      set({ anonymousBenchmarks: [...get().anonymousBenchmarks, benchmark] });
    } catch (error: any) {
      console.error('Add benchmark error:', error);
      set({ error: error.message });
      throw error;
    }
  },

  getRegionalBenchmarks: (location) => {
    return get().anonymousBenchmarks.filter(benchmark =>
      benchmark.location.toLowerCase().includes(location.toLowerCase())
    );
  },

  clearAllData: async () => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      // Supabase handles cascade deletes via ON DELETE CASCADE
      await supabase.from('profiles').delete().eq('user_id', user.id);

      set({
        profile: null,
        expenses: [],
        milestones: DEFAULT_MILESTONES,
        flightHours: {
          totalFlightHours: 0,
          totalGroundHours: 0,
          soloHours: 0,
          crossCountryHours: 0,
          nightHours: 0,
          instrumentHours: 0,
        },
        anonymousBenchmarks: [],
      });
    } catch (error: any) {
      console.error('Clear all data error:', error);
      set({ error: error.message });
    }
  },
}));

// Utility functions for calculations (unchanged)
export const calculateBudgetStats = (
  expenses: Expense[],
  totalBudget: number,
  flightHours: FlightHours
) => {
  const totalSpent = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const budgetRemaining = totalBudget - totalSpent;
  const budgetProgress = (totalSpent / totalBudget) * 100;
  const costPerHour = flightHours.totalFlightHours > 0
    ? totalSpent / flightHours.totalFlightHours
    : 0;

  const minHours = 40;
  const estimatedHoursRemaining = Math.max(0, minHours - flightHours.totalFlightHours);
  const projectedTotal = costPerHour > 0
    ? totalSpent + (costPerHour * estimatedHoursRemaining)
    : totalBudget;

  return {
    totalSpent,
    budgetRemaining,
    budgetProgress,
    costPerHour,
    projectedTotal,
  };
};

export const getCategoryBreakdown = (expenses: Expense[]) => {
  const breakdown: Record<ExpenseCategory, number> = {
    'Aircraft Rental': 0,
    'Instructor': 0,
    'Ground': 0,
    'Fuel': 0,
    'Materials': 0,
    'Exam': 0,
    'Medical': 0,
    'Checkride': 0,
    'Equipment': 0,
    'Misc': 0,
  };

  expenses.forEach(exp => {
    breakdown[exp.category] += exp.amount;
  });

  return breakdown;
};
